import React, { useState } from 'react';
import { z } from 'zod';
import { styled } from 'styled-components';
import type { BlockDefinition } from '../types';
import { checkFormHygiene } from '../formHygiene';
import { LeadFormFields } from './leadForm';

// Home form — clean rewrite of legacy molecules/HomeFormSection (Strapi
// sections.new-home-form-section). Lead-capture form that POSTs JSON to our
// same-origin /api/leads endpoint, which validates + proxies to Salesforce
// Web-to-Lead server-side. Fields are fixed (SF lead shape); the CMS drives the
// surrounding copy. No styled-system / next / recaptcha widget (token optional
// server-side). Submit state via client useState (SSR renders the empty form).
export const homeFormSchema = z.object({
  eyebrow: z.string().optional(),
  heading: z.string().min(1),
  description: z.string().optional(),
  bgImageUrl: z.string().optional(),
  // The CMS `form_text` is the consent/disclaimer line (not a button label). The
  // button label isn't CMS-driven on the live site — it's "Submit Now".
  consent: z.string().optional(),
  submitLabel: z.string().optional(), // legacy alias: existing content stored the consent text here
  // Optional "Schedule a call" button next to Submit. When set to a Calendly
  // URL, apps/web's client opens it as a popup (see apps/web/src/calendly.ts).
  scheduleUrl: z.string().optional(),
  scheduleLabel: z.string().optional(),
});

type HomeFormProps = z.infer<typeof homeFormSchema>;
type Status = 'idle' | 'submitting' | 'success' | 'error';

// Full-bleed band (spans the viewport width like live); content padded from edges.
const Section = styled.section<{ $bg?: string }>`
  width: 100%;
  margin: 4rem 0;
  padding: 4rem 1.6rem;
  color: #ffffff;
  /* Explicit background-image (blue overlay gradient + photo) + separate
     size/position/repeat — more robust than the comma-layered background
     shorthand, so the overlay reliably renders over the image. */
  background-image: ${({ $bg }) =>
    $bg
      ? `linear-gradient(90deg, rgba(6, 48, 145, 0.9), rgba(6, 48, 145, 0.6)), url(${$bg})`
      : 'linear-gradient(93deg, #063091 0%, #0a3aa8 100%)'};
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;

  @media (min-width: 64em) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 4rem;
    align-items: center;
    padding: 6.4rem clamp(3.2rem, 7vw, 16rem);
  }
`;

const Intro = styled.div``;

const Eyebrow = styled.p`
  margin: 0;
  font-family: 'DM Sans', sans-serif;
  font-size: 1.5rem;
  font-weight: 500;
  letter-spacing: 3px;
  text-transform: uppercase;
  opacity: 0.85;
`;

const Heading = styled.h2`
  margin: 0.8rem 0 0;
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 600;
  font-size: clamp(2.6rem, 4vw, 4.4rem);
`;

const Description = styled.p`
  margin: 1.2rem 0 0;
  font-family: 'DM Sans', sans-serif;
  font-size: 1.6rem;
  line-height: 1.7;
  opacity: 0.9;
`;

const FormCard = styled.form`
  display: grid;
  gap: 1.2rem;
  margin-top: 2.4rem;
  padding: 2.4rem;
  border-radius: 1.6rem;
  background: #ffffff;

  @media (min-width: 64em) {
    margin-top: 0;
  }
`;

const Row = styled.div`
  display: grid;
  gap: 1.2rem;

  @media (min-width: 48em) {
    grid-template-columns: 1fr 1fr;
  }
`;

const fieldStyles = `
  width: 100%;
  box-sizing: border-box;
  padding: 1.2rem 1.4rem;
  border: 1px solid #d7deec;
  border-radius: 1rem;
  font-family: 'DM Sans', sans-serif;
  font-size: 1.5rem;
  color: #0b1020;
  &:focus { outline: none; border-color: #063091; }
`;

const Input = styled.input`
  ${fieldStyles}
`;

const Textarea = styled.textarea`
  ${fieldStyles}
  min-height: 10rem;
  resize: vertical;
`;

// Off-screen honeypot; real users never see or fill it.
const Honeypot = styled.input`
  position: absolute;
  left: -9999px;
  width: 1px;
  height: 1px;
  opacity: 0;
`;

const Submit = styled.button`
  appearance: none;
  padding: 1.3rem 2.4rem;
  border: none;
  border-radius: 1.2rem;
  background: #063091;
  color: #ffffff;
  font-family: 'DM Sans', sans-serif;
  font-size: 1.6rem;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1.2rem;
`;

// "Schedule a call" — opens the Calendly popup (apps/web intercepts calendly.com
// links); degrades to opening the Calendly page directly before JS loads.
const ScheduleLink = styled.a`
  display: inline-flex;
  align-items: center;
  padding: 1.3rem 2.4rem;
  border-radius: 1.2rem;
  border: 1px solid rgba(255, 255, 255, 0.7);
  background: transparent;
  color: #ffffff;
  font-family: 'DM Sans', sans-serif;
  font-size: 1.6rem;
  font-weight: 600;
  text-decoration: none;
`;

const Field = styled.label`
  display: block;
`;

const Label = styled.span`
  display: block;
  margin-bottom: 0.6rem;
  color: #3a4256;
  font-family: 'DM Sans', sans-serif;
  font-size: 1.4rem;
`;

const Disclaimer = styled.p`
  margin: 0.4rem 0 0;
  color: #4e4f52;
  font-family: 'DM Sans', sans-serif;
  font-size: 1.4rem;
  line-height: 1.5;
`;

const Note = styled.p<{ $error?: boolean }>`
  margin: 0;
  font-family: 'DM Sans', sans-serif;
  font-size: 1.4rem;
  color: ${({ $error }) => ($error ? '#c0362c' : '#1a7f37')};
`;

function HomeForm({ eyebrow, heading, description, bgImageUrl, consent, submitLabel, scheduleUrl, scheduleLabel }: HomeFormProps) {
  const disclaimer = consent ?? submitLabel;
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState('');

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const formEl = e.currentTarget;
    const payload = Object.fromEntries(new FormData(formEl).entries()) as Record<string, string>;
    if ((payload.message ?? '').trim().length < 40) {
      setStatus('error');
      setError('Message must be at least 40 characters.');
      return;
    }
    // Client-side XSS / profanity guard (server re-checks authoritatively).
    const hy = checkFormHygiene([payload.title, payload.company, payload.phone, payload.message], [payload.firstName, payload.lastName]);
    if (!hy.ok) {
      setStatus('error');
      setError(hy.reason === 'malicious' ? 'Your submission contains characters that are not allowed.' : 'Please remove the offensive language from your submission.');
      return;
    }
    setStatus('submitting');
    setError('');
    try {
      const res = await fetch('/api/leads', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = (await res.json().catch(() => ({}))) as { ok?: boolean; message?: string };
      if (res.ok && data.ok) {
        setStatus('success');
        formEl.reset();
        // After the lead form, open scheduling (apps/web opens Calendly on this event).
        if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('cymmetri:calendly'));
      } else {
        setStatus('error');
        setError(data.message || 'Something went wrong. Please try again.');
      }
    } catch {
      setStatus('error');
      setError('Network error. Please try again.');
    }
  }

  return (
    <Section $bg={bgImageUrl}>
      <Intro>
        {eyebrow ? <Eyebrow>{eyebrow}</Eyebrow> : null}
        <Heading>{heading}</Heading>
        {description ? <Description>{description}</Description> : null}
      </Intro>

      {/* Shared cross-tenant lead form (packages/blocks/leadForm.tsx). Scheduling
          is handled by the post-submit Calendly (per-tenant), so no separate button. */}
      <LeadFormFields submitLabel="Submit Now" messageLabel="Message" />
      {disclaimer ? <Disclaimer>{disclaimer}</Disclaimer> : null}
    </Section>
  );
}

export const homeFormBlock: BlockDefinition<HomeFormProps> = {
  type: 'homeForm',
  schema: homeFormSchema,
  Renderer: HomeForm,
};
